export type Fetcher = (input: string, init?: RequestInit) => Promise<Response>;

/** A Tahi route error (conventions §14): body `{ error: code, message? }`. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly body: unknown = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiOptions {
  method?: string;
  json?: unknown;
  fetcher?: Fetcher;
}

export async function apiFetch<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const f: Fetcher = opts.fetcher ?? ((input, init) => globalThis.fetch(input, init));
  const hasBody = opts.json !== undefined;
  const res = await f(path, {
    method: opts.method ?? (hasBody ? 'POST' : 'GET'),
    credentials: 'include',
    headers: hasBody ? { 'content-type': 'application/json' } : undefined,
    body: hasBody ? JSON.stringify(opts.json) : undefined,
  });
  const text = await res.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }
  if (!res.ok) {
    const b = (body && typeof body === 'object' ? body : {}) as {
      error?: unknown;
      message?: unknown;
    };
    const code = typeof b.error === 'string' ? b.error : `http_${res.status}`;
    const message = typeof b.message === 'string' ? b.message : code;
    throw new ApiError(res.status, code, message, body);
  }
  return body as T;
}
